import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    MagnifyingGlassIcon,
    FunnelIcon,
    SparklesIcon,
    CpuChipIcon,
    FireIcon,
    ArrowTopRightOnSquareIcon,
    ChatBubbleLeftIcon,
    ChartBarIcon,
} from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import apiService from '../services/api';
import timeAgo from '../utils/timeAgo';
import REFRESH_INTERVALS, { useAutoRefresh } from '../config/refreshConfig';
import { useLanguage } from '../contexts/LanguageContext';
import { useDebounce } from '../hooks/useDebounce';
import { feedMarkdownComponents } from '../config/markdownComponents';
import { sentimentStyle, impactStyle, marketSentimentStyle, getRankColor } from '../utils/styleHelpers';
import ErrorMessage from '../components/ErrorMessage';
import LoadingSpinner from '../components/LoadingSpinner';

const SOURCES = ['All', 'reuters', 'bloomberg', 'wsj', 'cnbc', 'ft', 'techcrunch', 'theverge', 'arstechnica'];
const PAGE_SIZE = 20;

const card = {
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderRadius: '20px',
    border: '1px solid rgba(255, 255, 255, 0.5)',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.04)',
    padding: '1.25rem',
};

const FeedPage = () => {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const [cachedFeeds] = useState(() => apiService.peekCache('feeds:All'));
    const cachedFeedItems = useMemo(() => {
        const d = cachedFeeds;
        return d?.data?.feeds || d?.feeds || [];
    }, [cachedFeeds]);
    const [feeds, setFeeds] = useState(cachedFeedItems);
    const [loading, setLoading] = useState(!cachedFeeds);
    const [loadingMore, setLoadingMore] = useState(false);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [source, setSource] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);

    // Top banner + sidebar data
    const [cachedAISummary] = useState(() => apiService.peekCache('ai-events-summary:7'));
    const [cachedTechSummary] = useState(() => apiService.peekCache('tech-news-summary:7'));
    const [cachedHN] = useState(() => apiService.peekCache('hackernews'));
    const [aiEventsSummary, setAIEventsSummary] = useState(cachedAISummary?.data || cachedAISummary || null);
    const [techNewsSummary, setTechNewsSummary] = useState(cachedTechSummary?.data || cachedTechSummary || null);
    const [hackerNews, setHackerNews] = useState(() => {
        const data = cachedHN?.data || cachedHN;
        const items = data?.stories || data || [];
        return Array.isArray(items) ? items.slice(0, 15) : [];
    });
    const [topLoading, setTopLoading] = useState(!cachedAISummary && !cachedTechSummary && !cachedHN);
    const [feedError, setFeedError] = useState(null);

    // Debounced search query for auto-search
    const debouncedSearchQuery = useDebounce(searchQuery, 400);

    const hasDataRef = useRef(cachedFeedItems.length > 0);
    const fetchFeeds = useCallback(async (reset = false) => {
        const currentOffset = reset ? 0 : offset;
        if (reset) {
            if (!hasDataRef.current) setLoading(true);
            setFeedError(null);
        } else {
            setLoadingMore(true);
        }
        try {
            const params = { limit: PAGE_SIZE, offset: currentOffset };
            if (source !== 'All') params.source = source;
            const res = await apiService.getFeeds(params);
            const items = res?.data?.feeds || res?.feeds || [];
            if (reset) {
                setFeeds(items);
                setOffset(PAGE_SIZE);
            } else {
                setFeeds(prev => [...prev, ...items]);
                setOffset(currentOffset + PAGE_SIZE);
            }
            setHasMore(items.length >= PAGE_SIZE);
            if (items.length > 0) hasDataRef.current = true;
        } catch (err) {
            console.error('Failed to load feeds:', err);
            if (reset) {
                setFeedError(t('feed.loadError') || 'Failed to load feeds');
            }
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [offset, source, t]);

    const fetchTopData = useCallback(async () => {
        try {
            const [aiRes, techRes, hnRes] = await Promise.allSettled([
                apiService.getAIEventsSummary(7),
                apiService.getTechNewsSummary(7),
                apiService.getHackerNewsTop(),
            ]);

            if (aiRes.status === 'fulfilled') {
                setAIEventsSummary(aiRes.value?.data || aiRes.value);
            }
            if (techRes.status === 'fulfilled') {
                setTechNewsSummary(techRes.value?.data || techRes.value);
            }
            if (hnRes.status === 'fulfilled') {
                const data = hnRes.value?.data || hnRes.value;
                const items = data?.stories || data || [];
                setHackerNews(Array.isArray(items) ? items.slice(0, 15) : []);
            }
        } catch (err) {
            console.error('Failed to load top data:', err);
        } finally {
            setTopLoading(false);
        }
    }, []);

    const handleSearch = useCallback(async () => {
        if (!searchQuery.trim()) {
            fetchFeeds(true);
            return;
        }
        setIsSearching(true);
        setLoading(true);
        try {
            const res = await apiService.searchFeeds(searchQuery.trim(), 20);
            const items = res?.data?.results || res?.results || [];
            setFeeds(Array.isArray(items) ? items : []);
            setHasMore(false);
        } catch (err) {
            console.error('Search failed:', err);
        } finally {
            setLoading(false);
            setIsSearching(false);
        }
    }, [searchQuery, fetchFeeds]);

    useEffect(() => {
        fetchFeeds(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [source]);

    useEffect(() => {
        fetchTopData();
    }, [fetchTopData]);

    // Auto-search when debounced query changes (skip initial mount)
    const isInitialMount = React.useRef(true);
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        if (debouncedSearchQuery.trim()) {
            handleSearch();
        } else if (debouncedSearchQuery === '' && searchQuery === '') {
            // Only reset to full feed if search was cleared
            fetchFeeds(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchQuery]);

    useAutoRefresh(() => {
        if (!searchQuery.trim()) fetchFeeds(true);
    }, REFRESH_INTERVALS.feed.feeds);
    useAutoRefresh(fetchTopData, REFRESH_INTERVALS.feed.topData);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleSearch();
    };

    const cardHeader = (icon, title, color) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            {React.cloneElement(icon, { style: { width: '20px', height: '20px', color } })}
            <span style={{ fontSize: '0.95rem', fontWeight: '700', color: '#1D1D1F' }}>{title}</span>
        </div>
    );

    const loadingSpinner = <LoadingSpinner size="small" />;

    const summaryContent = (text, maxH = 'none') => (
        <div style={{
            fontSize: '0.85rem', color: '#333', lineHeight: '1.65',
            maxHeight: maxH, overflowY: maxH !== 'none' ? 'auto' : 'visible',
        }}>
            <ReactMarkdown components={feedMarkdownComponents}>{text}</ReactMarkdown>
        </div>
    );


    const badge = (text, style) => (
        <span style={{
            display: 'inline-block', padding: '2px 8px', borderRadius: '6px',
            fontSize: '0.72rem', fontWeight: '600', ...style,
        }}>{text}</span>
    );

    const hasMetrics = (m) => m && (m.revenue || m.eps || m.stock_change || (m.key_numbers && m.key_numbers.length > 0));

    const renderFeedCard = (feed, idx) => (
        <div
            key={feed.id || idx}
            style={{
                padding: '1.25rem', marginBottom: '12px', borderRadius: '16px',
                backgroundColor: 'rgba(255,255,255,0.5)', border: '1px solid rgba(0,0,0,0.04)',
                transition: 'box-shadow 0.15s ease',
            }}
        >
            {/* Header: source · time · relevance · sentiment */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.78rem', color: '#86868B' }}>
                    <span style={{ color: '#007AFF', fontWeight: '700', textTransform: 'uppercase', fontSize: '0.72rem' }}>
                        {typeof feed.feed_source === 'string' ? feed.feed_source : (typeof feed.source === 'string' ? feed.source : '')}
                    </span>
                    <span>·</span>
                    <span>{timeAgo(feed.published_at)}</span>
                    {feed.relevance_score != null && (
                        <>
                            <span>·</span>
                            <span style={{
                                fontWeight: '600',
                                color: feed.relevance_score >= 0.7 ? '#34C759' : feed.relevance_score >= 0.4 ? '#FF9F0A' : '#FF3B30',
                            }}>
                                {Math.round(feed.relevance_score * 100)}%
                            </span>
                        </>
                    )}
                </div>
                {typeof feed.sentiment?.label === 'string' && badge(feed.sentiment.label, sentimentStyle(feed.sentiment.label))}
            </div>

            {/* Title */}
            <div
                style={{ fontSize: '1.02rem', fontWeight: '650', lineHeight: '1.4', color: '#1D1D1F', marginBottom: '8px', cursor: 'pointer' }}
                onClick={() => feed.id ? navigate(`/feed/${feed.id}`) : feed.url && window.open(feed.url, '_blank')}
            >
                {typeof feed.title === 'string' ? feed.title : ''}
            </div>

            {/* AI Summary */}
            {typeof feed.ai_summary === 'string' && feed.ai_summary && (
                <div style={{ marginBottom: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '5px' }}>
                        <SparklesIcon style={{ width: '14px', height: '14px', color: '#007AFF' }} />
                        <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#007AFF' }}>{t('feed.aiSummary')}</span>
                    </div>
                    <div style={{
                        background: 'rgba(0,122,255,0.05)', borderLeft: '3px solid #007AFF',
                        borderRadius: '0 10px 10px 0', padding: '10px 12px',
                        fontSize: '0.83rem', color: '#444', lineHeight: '1.6',
                    }}>
                        {feed.ai_summary}
                    </div>
                </div>
            )}

            {/* Key Metrics */}
            {hasMetrics(feed.key_metrics) && (
                <div style={{ marginBottom: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '5px' }}>
                        <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#34C759' }}>{t('feed.keyMetrics')}</span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {feed.key_metrics.revenue && (
                            <span style={{ padding: '2px 8px', borderRadius: '6px', fontSize: '0.72rem', fontWeight: '500', background: 'rgba(52,199,89,0.08)', color: '#333' }}>
                                {t('feed.revenue')}: {typeof feed.key_metrics.revenue === 'object' ? feed.key_metrics.revenue.value : feed.key_metrics.revenue}
                            </span>
                        )}
                        {feed.key_metrics.eps && (
                            <span style={{ padding: '2px 8px', borderRadius: '6px', fontSize: '0.72rem', fontWeight: '500', background: 'rgba(52,199,89,0.08)', color: '#333' }}>
                                {t('feed.eps')}: {typeof feed.key_metrics.eps === 'object' ? feed.key_metrics.eps.value : feed.key_metrics.eps}
                            </span>
                        )}
                        {feed.key_metrics.stock_change && (typeof feed.key_metrics.stock_change === 'string' || typeof feed.key_metrics.stock_change === 'number') && (
                            <span style={{
                                padding: '2px 8px', borderRadius: '6px', fontSize: '0.72rem', fontWeight: '600',
                                background: String(feed.key_metrics.stock_change).startsWith('-') ? 'rgba(255,59,48,0.08)' : 'rgba(52,199,89,0.08)',
                                color: String(feed.key_metrics.stock_change).startsWith('-') ? '#FF3B30' : '#34C759',
                            }}>
                                {t('feed.stockPrice')}: {feed.key_metrics.stock_change}
                            </span>
                        )}
                        {feed.key_metrics.guidance && (
                            <span style={{ padding: '2px 8px', borderRadius: '6px', fontSize: '0.72rem', fontWeight: '500', background: 'rgba(88,86,214,0.08)', color: '#5856D6' }}>
                                {t('feed.guidance')}: {typeof feed.key_metrics.guidance === 'object' ? (feed.key_metrics.guidance.value || JSON.stringify(feed.key_metrics.guidance)) : feed.key_metrics.guidance}
                            </span>
                        )}
                    </div>
                </div>
            )}

            {/* Market Impact */}
            {feed.market_impact?.impact_level && (
                <div style={{ marginBottom: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '5px' }}>
                        <ChartBarIcon style={{ width: '14px', height: '14px', color: '#AF52DE' }} />
                        <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#AF52DE' }}>{t('feed.marketImpact')}</span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', fontSize: '0.75rem' }}>
                        {badge(`${t('feed.impactLevel')}: ${feed.market_impact.impact_level}`, impactStyle(feed.market_impact.impact_level))}
                        {feed.market_impact.market_sentiment && badge(`${t('feed.sentiment')}: ${feed.market_impact.market_sentiment}`, marketSentimentStyle(feed.market_impact.market_sentiment))}
                        {feed.market_impact.key_stocks && (
                            <span style={{ color: '#007AFF', fontWeight: '600', fontSize: '0.75rem' }}>
                                {t('feed.relatedStocks')}: {Array.isArray(feed.market_impact.key_stocks)
                                    ? feed.market_impact.key_stocks.filter(s => typeof s === 'string').join(', ')
                                    : (typeof feed.market_impact.key_stocks === 'object' ? Object.keys(feed.market_impact.key_stocks).join(', ') : '')}
                            </span>
                        )}
                    </div>
                </div>
            )}

            {/* Footer: category · author · view link */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '8px', borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {typeof feed.category === 'string' && feed.category && (
                        <span style={{
                            padding: '2px 8px', borderRadius: '6px', fontSize: '0.7rem', fontWeight: '600',
                            textTransform: 'uppercase', background: 'rgba(0,0,0,0.04)', color: '#86868B',
                        }}>
                            {feed.category}
                        </span>
                    )}
                    {typeof feed.author === 'string' && feed.author && (
                        <span style={{ fontSize: '0.75rem', color: '#86868B' }}>By {feed.author}</span>
                    )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {feed.id && (
                        <span
                            style={{ fontSize: '0.78rem', color: '#007AFF', fontWeight: '500', cursor: 'pointer' }}
                            onClick={(e) => { e.stopPropagation(); navigate(`/feed/${feed.id}`); }}
                        >
                            {t('feed.readMore')} →
                        </span>
                    )}
                    {feed.url && (
                        <span
                            style={{ fontSize: '0.78rem', color: '#86868B', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px' }}
                            onClick={(e) => { e.stopPropagation(); window.open(feed.url, '_blank'); }}
                        >
                            <ArrowTopRightOnSquareIcon style={{ width: '13px', height: '13px' }} />
                            {t('feed.original')}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <div>
            {/* ====== Top Row: AI 动态 + 科技最前线 (side by side) ====== */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' }}>
                {/* AI 动态 */}
                <div style={card}>
                    {cardHeader(<CpuChipIcon />, t('feed.aiTrends'), '#AF52DE')}
                    {topLoading ? loadingSpinner : aiEventsSummary?.summary ? (
                        <>
                            {summaryContent(aiEventsSummary.summary, '240px')}
                            {aiEventsSummary.event_count && (
                                <div style={{ fontSize: '0.75rem', color: '#86868B', marginTop: '10px', paddingTop: '8px', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                                    {t('feed.pastDays').replace('{days}', '7').replace('{count}', aiEventsSummary.event_count)}
                                </div>
                            )}
                        </>
                    ) : (
                        <div style={{ fontSize: '0.85rem', color: '#86868B' }}>{t('feed.noAiSummary')}</div>
                    )}
                </div>

                {/* 科技最前线 */}
                <div style={card}>
                    {cardHeader(<FireIcon />, t('feed.techFrontier'), '#FF3B30')}
                    {topLoading ? loadingSpinner : techNewsSummary?.summary ? (
                        <>
                            {summaryContent(techNewsSummary.summary, '240px')}
                            {techNewsSummary.news_count && (
                                <div style={{ fontSize: '0.75rem', color: '#86868B', marginTop: '10px', paddingTop: '8px', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                                    {t('feed.pastDaysTech').replace('{days}', '7').replace('{count}', techNewsSummary.news_count)}
                                </div>
                            )}
                        </>
                    ) : (
                        <div style={{ fontSize: '0.85rem', color: '#86868B' }}>{t('feed.noTechSummary')}</div>
                    )}
                </div>
            </div>

            {/* ====== Main Content: Feed List (left) + Sidebar (right) ====== */}
            <div style={{ display: 'flex', gap: '1.25rem' }}>
                {/* Left Panel - Feed List */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Filter Bar */}
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                        <FunnelIcon style={{ width: '18px', color: '#86868B' }} />
                        <select
                            style={{
                                padding: '8px 14px', borderRadius: '12px',
                                border: '1px solid rgba(0,0,0,0.08)', backgroundColor: '#fff',
                                fontSize: '0.85rem', color: '#1D1D1F', fontWeight: '500', cursor: 'pointer', outline: 'none',
                            }}
                            value={source}
                            onChange={(e) => setSource(e.target.value)}
                            aria-label="Filter by news source"
                        >
                            {SOURCES.map(s => (
                                <option key={s} value={s}>{s === 'All' ? t('feed.allSources') : s}</option>
                            ))}
                        </select>
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            backgroundColor: 'rgba(0,0,0,0.04)', borderRadius: '12px', padding: '8px 14px',
                            flex: 1, minWidth: '200px',
                        }}>
                            <MagnifyingGlassIcon style={{ width: '18px', color: '#86868B', flexShrink: 0 }} />
                            <input
                                style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: '0.9rem', color: '#1D1D1F', width: '100%' }}
                                placeholder={t('feed.searchPlaceholder')}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={handleKeyDown}
                                aria-label="Search news articles"
                                type="search"
                            />
                            {searchQuery && (
                                <button
                                    onClick={handleSearch}
                                    disabled={isSearching}
                                    style={{
                                        border: 'none', background: '#007AFF', color: '#fff',
                                        borderRadius: '8px', padding: '4px 12px', fontSize: '0.8rem',
                                        fontWeight: '600', cursor: 'pointer', flexShrink: 0,
                                    }}
                                >
                                    {isSearching ? '...' : 'Search'}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Feed List */}
                    <div style={{ ...card, padding: '1rem' }}>
                        {loading ? (
                            <LoadingSpinner text={t('feed.loadingFeeds')} size="large" />
                        ) : feedError ? (
                            <div style={{ padding: '2rem' }}>
                                <ErrorMessage message={feedError} onRetry={() => fetchFeeds(true)} />
                            </div>
                        ) : feeds.length === 0 ? (
                            <div style={{ textAlign: 'center', color: '#86868B', padding: '3rem 0' }}>
                                {t('feed.noFeeds')}
                            </div>
                        ) : (
                            feeds.map((feed, idx) => renderFeedCard(feed, idx))
                        )}

                        {hasMore && !loading && feeds.length > 0 && (
                            <button
                                onClick={() => fetchFeeds(false)}
                                disabled={loadingMore}
                                style={{
                                    display: 'block', margin: '1rem auto 0.5rem', padding: '10px 32px',
                                    backgroundColor: '#1D1D1F', color: '#fff', border: 'none',
                                    borderRadius: '20px', fontSize: '0.9rem', fontWeight: '600',
                                    cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                }}
                            >
                                {loadingMore ? t('feed.loadingMore') : t('feed.loadMore')}
                            </button>
                        )}
                    </div>
                </div>

                {/* Right Panel - Today's Dashboard + HackerNews */}
                <div style={{ width: '360px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                    {/* HackerNews 热门 */}
                    <div style={card}>
                        {cardHeader(<ArrowTopRightOnSquareIcon />, t('feed.hotTech'), '#FF6600')}
                        {topLoading ? loadingSpinner : hackerNews.length > 0 ? (
                            <div>
                                {hackerNews.map((item, idx) => (
                                    <div
                                        key={item.id || idx}
                                        style={{
                                            display: 'flex', gap: '10px', padding: '8px 0',
                                            borderBottom: idx < hackerNews.length - 1 ? '1px solid rgba(0,0,0,0.04)' : 'none',
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => item.url && window.open(item.url, '_blank')}
                                    >
                                        <span style={{
                                            fontSize: '0.85rem', fontWeight: '700', width: '24px',
                                            textAlign: 'center', flexShrink: 0, color: getRankColor(idx + 1),
                                        }}>
                                            {idx + 1}
                                        </span>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '0.82rem', fontWeight: '500', color: '#1D1D1F', lineHeight: '1.4' }}>
                                                {typeof item.title === 'string' ? item.title : ''}
                                            </div>
                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '0.7rem', color: '#86868B', marginTop: '4px' }}>
                                                <span>{item.score || item.points} pts</span>
                                                <ChatBubbleLeftIcon style={{ width: '12px', height: '12px' }} />
                                                <span>{item.descendants || item.comments || 0}</span>
                                                <span>{timeAgo(item.time ? new Date(item.time * 1000).toISOString() : item.created_at)}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div style={{ fontSize: '0.85rem', color: '#86868B' }}>{t('feed.noHackerNews')}</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FeedPage;
